import {
  AwatingList,
  LastOrderDraft,
  OrderDirection,
  OrderDirectionCalendar,
  OrderForm,
  OrderPrice,
  OrderPriceCurrency,
  OrderService,
  OrderView,
  OrderWarnings,
  TimetableDate,
  TradingNetwork
} from '~/types/types'

import { OrderType } from "~/types/store"
import {deepCopyObject, promiseDelay} from '~/common/functions'
import {
  convertPriceServices,
  convertScheduller,
  defaultOrder,
  fakeLocationText,
  getCountryFlagIcon,
  getCurrencyText,
  pricePayload,
  convertTimeTableV3,
  generalVolumeToPrice,
  generalWeightToPrice,
  generalCountToPrice, parcelTypes,
} from '@/common/objects'
import {getWaitingData, getOrderPrice as getOrderPriceApi, getLoadingManifest} from '~/api/order'
import {getTradingNetworkAddressList, getTradingNetworkList} from '~/api/trading-network'
import {getTemplate} from '~/api/template';
import {createDraft as createDraftApi, updateDraft} from '~/api/draft';
import {formatDate} from '~/common/date';
import {useLayoutStore} from '~/stores/personal/layout';
import {useSessionStorage, asyncComputed} from '@vueuse/core';
import {mergeOrderV3WithOrder, priceDataToOrderFormV3} from '~/common/objectsV3';
import {TemplateOrder} from '~/types/order/orderV3';
import {LAST_ORDER_DRAFT_KEY, LoadingManifest} from '~/types/order/order';
import {storeToRefs} from 'pinia';
import useOrderTimeTable from '~/composables/order/useOrderTimeTable';
import {useCityStore} from '~/stores/city';
import useOrderConditions from '~/composables/order/useOrderConditions';
import useMultiOrder from '~/composables/order/useMultiOrder';
import {computed} from "vue";

export const useOrderStore = defineStore('order', () => {
  const { isEditOrder, isEditAddressModal } = storeToRefs(useLayoutStore())
  const { locationByGuid } = useCityStore()
  const { $showToast } = useNuxtApp()

  const hideOrderWrappingOverload = useSessionStorage('hide-order-wrapping-overload', false)
  const hideOrderWrappingTurnOver = useSessionStorage('hide-order-wrapping-tern-over', false)

  const lastOrderDraftData = useSessionStorage<LastOrderDraft>(
      LAST_ORDER_DRAFT_KEY,
      null,
      { serializer: {read: JSON.parse, write: JSON.stringify} } // иначе не работает
  )
  let priceAbortController = new AbortController()

  // Счётчик запросов на цену, если будет больше порогового, то заказ будет сохранятся в localStorage
  const getPriceRequestCount = ref(0)
  const orderType = ref<OrderType>('individual')
  const orderTotalPriceLoader = ref(false)
  const isCalendarLocked = ref(false)
  const isTemplate = ref(false)
  const appliedTemplates = ref<number[]>([])
  const locationMaxFloor = ref({
    dispatch: 0,
    destination: 0
  })
  const waitingTimeList = ref({} as AwatingList)
  const services = ref<OrderService[]>([])
  const orderPrice = ref<OrderPrice|null>(null)
  const orderWarnings = ref<OrderWarnings>([])
  const orderFormV3 = ref(priceDataToOrderFormV3())
  const orderForm = ref(defaultOrder)
  const orderView = ref(deepCopyObject(orderForm.value) as OrderView)
  const lightOrderStep = ref(0)
  const loadingManifest = ref<LoadingManifest[]>()
  const isLoadingFullPrice = ref(false)

  const dispatchLocation = asyncComputed(async () => {
    const source = isTemplate.value ? orderFormV3.value : orderForm.value
    const res = await locationByGuid(source.directions.dispatch.locationGuid)
    orderForm.value.specialRequirements.dispatch.entryFee.currency = res.country || ''
    return res
  })

  const destinationLocation = asyncComputed(async () => {
    const source = isTemplate.value ? orderFormV3.value : orderForm.value
    const res = await locationByGuid(source.directions.destination.locationGuid)
    orderForm.value.specialRequirements.destination.entryFee.currency = res.country || ''
    return res
  })

  const networkingAddress = asyncComputed(async () => {
    const source = isTemplate.value ? orderFormV3.value : orderForm.value
    const networkGuid = source.directions.destination.networkGuid
    const guid = isTemplate.value
        ? orderFormV3.value.directions.destination.guid
        : orderForm.value.directions.destination.networkAddressGuid
    if (!networkGuid || !guid) {
      return null
    }
    return (await getTradingNetworkAddressList({ search: guid, networkGuid }))?.[0]
  }, null, { lazy: true })

  const {
    timetable,
    timetableData,
    timetablePayload,
    destinationGroupDatesIndex,
    skipDefaultTimeFlag,
    splitTime,
    splitIndex,
    updateSplit
  } = useOrderTimeTable(orderForm, dispatchLocation, destinationLocation)

  const { orderConditionValues, orderCreateContent, orderCargoConditionHint } = useOrderConditions(orderForm)
  const multiOrder = useMultiOrder(orderForm)

  const placeItems = computed(() => {
    return orderForm.value.cargo.places.items
  })

  const orderSource = computed(() => {
    return (isEditOrder.value || isEditAddressModal.value) ? orderForm.value : orderView.value
  })

  const orderTotalPrice = computed(() => {
    return orderSource.value.totalPrice
  })

  const isDetailCorrectOrderView = computed(() => {
    return !!orderView.value.meta.orderState
  })

  const codList = computed(() => {
    return orderForm.value?.meta?.codList || []
  })

  const codDate = computed(() => {
    return orderForm.value?.meta?.codList?.shop?.date || ''
  })

  const codOrder = computed(() => {
    return orderForm.value?.meta?.codList?.shop?.order || ''
  })

  const payerText = computed(() => {
    const customer: any = orderForm.value?.customer
    const name = customer?.[customer.payer].corporation?.companyName.value ||
        customer?.[customer.payer].individual?.name.value || ''
    return name ? `Плательщиком заказа является ${name}` : ''
  })

  const isMultiOrder = computed(() => {
    return orderType.value === 'multi' && multiOrder.selectedRequestId.value === 0
  })

  const isNetworkDisabled = computed(() => {
    const networkAddressGuid = orderForm.value.directions.destination.networkGuid
          ? orderForm.value.directions.destination.networkAddressGuid || orderForm.value.directions.destination.address || ''
          : ''

    return orderType.value === 'networking' && !networkAddressGuid
  })

  const isPupDisabled = computed(() => {
    return orderType.value === 'individual' &&
        orderForm.value.directions.destination.direction === 'pickupPoint' &&
        !orderForm.value.directions.destination.pickupPointGuid
  })

  const generalVolumeV2 = computed(() => {
    let volume = 0
    const mode = orderForm.value.cargo.mode
    const minVolume = 0.01

    if (mode === 'dimension') {
      volume = orderForm.value.cargo.dimensions.general.volume
    } else {
      // TODO: any и убрать копипасту vz-cargo-general, или убрать это когда пвз яндекс будет не актуален
      volume = (orderForm.value.cargo[mode].items as any).reduce((preVal: number, item: any) => {
        if (mode === 'places' || mode === 'pallets') {
          preVal += (item.length * item.width * item.height * item.count)
        } else if (mode === 'parcels') {
          preVal += parcelTypes[item.type].volume * parseInt(item.count)
        }
        return preVal
      }, 0).toFixed(2)
      volume = (volume < minVolume) ? minVolume : volume
    }
    return +volume
  })

  // TODO: убрать копипасту vz-cargo-general, или убрать это когда пвз яндекс будет не актуален
  const generalWeightV2 = computed(() => {
    let weight = 0
    const mode = orderForm.value.cargo.mode
    if(mode === 'dimension') {
      weight = orderForm.value.cargo.dimensions.general.weight
    }
    else {
      weight = (orderForm.value.cargo[mode].items as any).reduce(function (preVal: number, item: any) {
        preVal += parseFloat(item.weight) * parseInt(item.count)
        return preVal
      }, 0).toFixed(1)
    }
    return weight
})

  // Габариты груза подходят под доставку в ПВЗ
  const hasPupCost = computed(() => {
    return orderPriceCurrency.value.currency === '₽' && specialPupPvz.value
  })

  const specialPupPvz = computed(() => {
    return orderForm.value.cargo.dimensions.detail.weight <= 30 &&
      orderForm.value.cargo.dimensions.detail.length <= 1 &&
      orderForm.value.cargo.dimensions.detail.height <= 1 &&
      orderForm.value.cargo.dimensions.detail.width <= 1 &&
      generalVolumeV2.value <= 1 &&
      generalWeightV2.value <= 30
  })

  // Ложные локации
  const isFakeLocationDisabled = computed(() => {
    if (isNetworkDisabled.value) {
      return false
    }
    return Number(dispatchLocation.value?.fake) === 1 || Number(destinationLocation.value?.fake) === 1
  })

  const costDisabledReason = computed(() => {
    if (isNetworkDisabled.value) {
      return 'Стоимость будет отражаться после выбора Торговой сети и Филиала'
    }
    if (isPupDisabled.value) {
      return 'Стоимость будет отражаться после выбора Пункта выдачи'
    }
    if (isFakeLocationDisabled.value) {
      return fakeLocationText
    }
    if (isMultiOrder.value && !multiOrder.requestList.value.length) {
      return 'Добавьте адрес доставки'
    }
    return ''
  })

  const orderPriceCurrency = computed((): OrderPriceCurrency => {
    const isBY = orderForm.value?.currency === 'BY'
    const isKZ = orderForm.value?.currency === 'KZ'
    const isKG = orderForm.value?.currency === 'KG'
    const hasBYCurrency = orderTotalPrice.value?.currency?.hasOwnProperty('BY')
    const hasKZCurrency = orderTotalPrice.value?.currency?.hasOwnProperty('KZ')
    const hasKGCurrency = orderTotalPrice.value?.currency?.hasOwnProperty('KG')

    const currency = getCurrencyText(orderForm.value?.currency, orderForm.value?.currency === 'RU')
    const flag = getCountryFlagIcon(orderForm.value?.currency)

    return {
      flag,
      currency,
      isBY,
      isKZ,
      isKG,
      hasBYCurrency,
      hasKZCurrency,
      hasKGCurrency
    }
  })

  /** Старые даты отправки/прибытия, при редактировании заказа */
  const orderDates = computed((): { dispatch?: OrderDirectionCalendar, destination?: OrderDirectionCalendar } => {
    if (!orderForm.value.meta.guid || orderForm.value.meta.guid !== orderView.value.meta.guid) {
      return {}
    }
    function getDirectionData(direction: OrderDirection, useSplit = false) {
      return JSON.stringify([
        useSplit
            ? direction.direction === 'address' ? direction.calendar.to : direction.calendar.terminalTime
            : '',
        direction.direction,
        direction.locationGuid,
        direction.direction === 'address' ? '' : direction.direction === 'terminal' ? direction.terminalGuid : direction.pickupPointGuid
      ])
    }
    const dispatchViewData = getDirectionData(orderView.value.directions.dispatch, true)
    const dispatchFormData = getDirectionData(orderForm.value.directions.dispatch, true)
    const destinationViewData = getDirectionData(orderView.value.directions.destination)
    const destinationFormData = getDirectionData(orderForm.value.directions.destination)
    // console.log(dispatchViewData === dispatchFormData || { dispatchViewData, dispatchFormData })
    // console.log(destinationViewData === destinationFormData || { destinationViewData, destinationFormData })
    if (dispatchViewData !== dispatchFormData) {
      const dispatch = orderForm.value.directions.dispatch
      const time = dispatch.direction === 'address' ? dispatch.calendar.to : dispatch.calendar.terminalTime
      // Если есть время и значит оно отличается, или если отличается вообще чем-то другим
      // Т.е. если нет времени или оно не менялось, то будет показываться даты/время из заказа как валидные
      if (time || getDirectionData(orderView.value.directions.dispatch) !== getDirectionData(orderForm.value.directions.dispatch)) {
        return {}
      }
    }

    return  {
      dispatch: orderView.value.directions.dispatch.calendar,
      destination: destinationViewData === destinationFormData
        ? orderView.value.directions.destination.calendar
        : undefined
    }
  })

  /** actions */
  function updateTimeTable(): void {
    if (!timetableData.value) {
      timetable.value = null
      return
    }
    if (isMultiOrder.value) {
      multiOrder.updateMultiOrderTimetable(splitTime.value)
    }

    // Если заказ заблокирован, то оставляем даты на месте. Иначе при возможности оставляем дату прибытия.
    // Чтобы ставить дату прибытия по умолчанию - перед запросом можно её очистить
    const isOrderDispatch = (orderDates.value.dispatch?.date === orderForm.value.directions.dispatch.calendar.date)
    const hardDispatch = !(isCalendarLocked.value || orderForm.value.meta?.lockState?.dispatch || isOrderDispatch)
    const tt = convertTimeTableV3(deepCopyObject(timetableData.value), {
      dispatchCalendar: !hardDispatch ? orderForm.value.directions.dispatch.calendar : undefined,
      destinationCalendar: orderForm.value.directions.destination.calendar,
      softDestinationDate: !isCalendarLocked.value && !orderForm.value.meta?.lockState?.destination,
      orderDates: {
        dispatch: orderDates.value.dispatch,
        destination: isOrderDispatch
            ? orderDates.value.destination
            : undefined
      }
    })

    const dispatchDate = tt?.dispatch?.find(d => d.isDefault) as TimetableDate
    updateSplit(tt)
    const destinationDate = tt?.destination?.[destinationGroupDatesIndex.value || 0]?.dates?.find(d => d.isDefault) as TimetableDate
    if(!destinationDate) {
      console.error('[error] destinationDate - undefined, 1с не отдала в timetable -> destination -> dates')
      return;
    }

    // Заполнение пустых дат
    if (!orderForm.value.directions.dispatch.calendar.date || (!orderForm.value.directions.destination.calendar.date && !isMultiOrder.value)) {
      if (dispatchDate && !orderForm.value.directions.dispatch.calendar.date) {
        orderForm.value.directions.dispatch.calendar.date = formatDate(dispatchDate.date, 'YYYY-MM-DD')
        orderForm.value.directions.dispatch.calendar.from = formatDate(dispatchDate.optimalTime.from, 'HH:mm')
        orderForm.value.directions.dispatch.calendar.to = formatDate(dispatchDate.optimalTime.to, 'HH:mm')
        orderForm.value.directions.dispatch.calendar.terminalTime = tt?.destination?.[destinationGroupDatesIndex.value || 0]?.groupTime
      }
      if (destinationDate && !orderForm.value.directions.destination.calendar.date) {
        updateSplit(tt)
        orderForm.value.directions.destination.calendar.date = formatDate(destinationDate.date, 'YYYY-MM-DD')
        orderForm.value.directions.destination.calendar.from = formatDate(destinationDate.optimalTime.from, 'HH:mm')
        orderForm.value.directions.destination.calendar.to = formatDate(destinationDate.optimalTime.to, 'HH:mm')
        orderForm.value.directions.destination.calendar.terminalTime = formatDate(destinationDate.optimalTime.from, 'HH:mm')
      }
      if (dispatchDate || destinationDate) {
        return updateTimeTable()
      }
    }

    // Если convertTimeTable вернул другие даты, то меняем даты и опять прогоняем через convertTimeTable без изменения timetable
    // Если даты совподают с датами заказа, то не меняем
    if (
        orderForm.value.directions.dispatch.calendar.date !== orderDates.value.dispatch?.date &&
        orderForm.value.directions.destination.calendar.date !== orderDates.value.destination?.date &&
        (orderForm.value.directions.dispatch.calendar.date !== dispatchDate?.date || orderForm.value.directions.destination.calendar.date !== destinationDate?.date)
    ) {
      if (dispatchDate?.date) {
        orderForm.value.directions.dispatch.calendar.date = dispatchDate.date
      }
      if (destinationDate?.date) {
        orderForm.value.directions.destination.calendar.date = destinationDate.date
      }
      // Если нет прибытия, то это сломанное расписание. Дальше выставятся рабочие данные (смена с адреса на терминал напр.)
      if (timetableData.value.destination && !isMultiOrder.value) {
        return updateTimeTable()
      }
    }

    timetable.value = tt
  }

  function setOrderPrice(data?: OrderPrice) {
    orderPrice.value = data || null
  }

  function setOrderForm(state: OrderForm): void {
    orderForm.value = state
  }

  async function setOrderType(value: OrderType, isSpecialChangeNetwork?: boolean) {
    await onOrderTypeChange(value, isSpecialChangeNetwork)
    orderType.value = value
  }

  async function onOrderTypeChange(value: OrderType, isSpecialChangeNetwork?: boolean) {
    orderForm.value.isNetwork = value !== 'individual'

    if (value === 'individual') {
      orderForm.value.directions.destination.networkAddressGuid = ''
      orderForm.value.customer.destination.networking = false
    } else if (value === 'networking') {
      if (orderForm.value.cargo.mode === 'parcels') {
        orderForm.value.cargo.dimensions.general.volume = generalVolumeToPrice(orderForm.value)
        orderForm.value.cargo.dimensions.general.weight = generalWeightToPrice(orderForm.value)
        orderForm.value.cargo.dimensions.general.place = generalCountToPrice(orderForm.value)

        orderForm.value.cargo.mode = 'dimension'
      }

      if (orderForm.value.customer.payer === 'destination' || orderForm.value.customer.payer === 'segregated') {
        orderForm.value.customer.payer = 'dispatch'
      }
    }

    const taxId = orderForm.value.customer.destination.corporation.taxId
    const address = orderForm.value.directions.destination.address
    if (orderForm.value.customer.destination.type === 'corporation' && taxId) {
      const tradingNetworks = await getTradingNetworkList({ taxId })
      if (tradingNetworks[0]?.guid) {
        const objNetwork = {
          ...tradingNetworks[0],
          addressGuid: orderForm.value.directions.destination.networkAddressGuid,
          address: ''
        }
        if (value === 'networking' && address && !objNetwork.addressGuid) {
          objNetwork.addressGuid = (await getTradingNetworkAddressList({
            networkGuid: objNetwork.guid,
            search: address
          }))?.find(item => item.address === address )?.guid
        }
        if(!isSpecialChangeNetwork && !objNetwork.addressGuid) {
          objNetwork.address = address
        }
        setTradingNetwork(objNetwork)
      } else {
        orderForm.value.directions.destination.networkGuid = ''
        orderForm.value.directions.destination.networkAddressGuid = ''
      }
    }
  }

  function setTradingNetwork(obj: TradingNetwork & { address?: string, addressGuid?: string }): void {
    const newData = deepCopyObject(orderForm.value)

    newData.directions.destination.networkAddressGuid = obj.addressGuid || ''
    newData.directions.destination.address = obj.address || ''
    newData.directions.destination.isCustom = !!obj.address && !obj.addressGuid
    newData.directions.destination.hasCcbs = obj.hasCcbs
    newData.directions.destination.networkGuid = obj.guid
    newData.customer.destination = {
      ...newData.customer.destination,
      networking: true,
      type: 'corporation',
      corporation: {
        email: obj.contact.email,
        taxId: obj.defaultTaxId,
        IEC: '',
        phone: [obj.contact.phone],
        name: obj.contact.name,
        companyName: {
          id: '',
          value: obj.name
        }
      }
    }

    setOrderForm(newData)
  }

  function setTradingNetworkV3(obj: TradingNetwork & { address?: string, addressGuid?: string }): void {
    if (orderFormV3.value.directions) {
      orderFormV3.value.directions.destination.guid = obj.addressGuid || ''
      orderFormV3.value.directions.destination.address = obj.address || ''
      orderFormV3.value.directions.destination.isCustom = !!obj.address && !obj.addressGuid
      orderFormV3.value.directions.destination.networkGuid = obj.guid
    }
    if (orderFormV3.value.customer) {
      orderFormV3.value.customer.destination = {
        ...orderFormV3.value.customer.destination,
        entityType: 'corporation',
        corporation: {
          email: obj.contact.email,
          taxId: obj.defaultTaxId,
          IEC: '',
          contact: {
            phone: [obj.contact.phone],
            name: obj.contact.name,
            email: obj.contact.email
          },
          companyName: obj.name,
          networking: true,
        } as any
      }
    }
  }

  function setOrderWarnings(state: OrderWarnings): void {
    if (JSON.stringify(state) === JSON.stringify(orderWarnings.value)) {
      return
    }

    orderWarnings.value = state
    orderWarnings.value.forEach((warning) => {
      $showToast({
        variant: 'error',
        title: warning.title,
        text: warning.message,
        autocloseDelay: 10000,
      })
    })
  }

  function setDriverData(driverPassNote: any) {
    orderForm.value.specialRequirements.dispatch.driverData.passNote = driverPassNote?.dispatch
    orderForm.value.specialRequirements.destination.driverData.passNote = driverPassNote?.destination
  }

  async function setEmptyOrder() {
    isCalendarLocked.value = false
    orderType.value = 'individual'
    setOrderForm(deepCopyObject(defaultOrder))
    orderFormV3.value = priceDataToOrderFormV3()
    appliedTemplates.value = []
    loadingManifest.value = undefined
    multiOrder.clear()
  }

  function setDefaultDate() {
    orderForm.value.directions.destination.calendar.date = ''
    updateSplit()
  }

  function updateOrderPrice(priceData: { services?: any, shippingDiagram?: any, cost?: any }): void {
    orderSource.value.services = convertPriceServices(priceData)
    if (priceData.shippingDiagram) {
      orderSource.value.shippingDiagram = convertScheduller(priceData.shippingDiagram)
    }

    if (priceData.cost) {
      orderSource.value.totalPrice = priceData.cost
      priceData.services.forEach((service: any) => {
        if (service.insuranceStd) {
          orderForm.value.cargo.insurance.insuranceStd = service.insuranceStd
        }
      })

      if (!!orderForm.value.depositBonusFromDiscount) {
        orderForm.value.depositBonusFromDiscount = priceData.cost.depositBonusFromDiscount || priceData.cost.savedBonusFromDiscount || 0
      }
    }

    const segregatedList: any = {}
    orderForm.value.services?.forEach((service: OrderService) => {
      segregatedList[service.guid] = orderForm.value.customer.segregated.list[service.guid] || service.payer
    })
    orderForm.value.customer.segregated.list = segregatedList
  }

  async function getOrderPriceAction(): Promise<void> {
    if (isTemplate.value || !!costDisabledReason.value) {
      return
    }

    orderTotalPriceLoader.value = true
    isLoadingFullPrice.value = true
    try {
      if (isMultiOrder.value) {
        await multiOrder.getMultiPrice()
        await getOrderFullPriceAction()
        return
      }

      priceAbortController.abort('Прервано')
      priceAbortController = new AbortController()
      const data = await getOrderPriceApi(pricePayload(orderForm.value), undefined, priceAbortController.signal)
      isLoadingFullPrice.value = false
      orderTotalPriceLoader.value = false

      if (Object.keys(data).length === 1 && 'statusCode' in data) {
        data.error = {
          opt: { message: { message: 'Неизвестная ошибка', description: `Статус: ${data.statusCode}` } }
        }
      }

      if (data.error) {
        $showToast({
          variant: 'error',
          title: data.error.opt.message?.message || data.error.message,
          text: data.error.opt.message?.description,
          iconName: 'alert'
        })
        return
      }

      updateOrderPrice(data)
      setOrderWarnings(data.warnings || [])
      setDriverData(data.driverPassNote)

      const currency = (orderTotalPrice.value?.currency?.BY !== undefined && orderTotalPrice.value?.currency?.BY)
        ? 'BY' : (orderTotalPrice.value?.currency?.KZ !== undefined && orderTotalPrice.value?.currency?.KZ)
        ? 'KZ' : (orderTotalPrice.value?.currency?.KG !== undefined && orderTotalPrice.value?.currency?.KG)
        ? 'KG' : 'RU'
      orderForm.value.currency = currency
      orderForm.value.defaultCurrency = orderTotalPrice.value?.currency?.default

      await getOrderFullPriceAction()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
    } finally {
      orderTotalPriceLoader.value = false
      isLoadingFullPrice.value = false
    }
    saveToStorage()
  }

  async function getOrderFullPriceAction() {
    const dispatch = await locationByGuid(orderForm.value.directions.dispatch.locationGuid)
    const destination = await locationByGuid(orderForm.value.directions.destination.locationGuid)
    isLoadingFullPrice.value = true
    await promiseDelay(500)
    const priceData = await getOrderPriceApi(pricePayload({
      ...orderForm.value,
      fullService: true
    }, { dispatch: dispatch.terminal_only === '1', destination: destination.terminal_only === '1' }))
    isLoadingFullPrice.value = false
    setOrderPrice(priceData)
  }

  async function applyTemplate(template: Partial<TemplateOrder>, notify = false) {
    if (template.id) {
      template.content = (await getTemplate(template.id, true)).content
    }

    orderForm.value = mergeOrderV3WithOrder(orderForm.value, template.content)

    if (
        orderType.value === 'networking' && !template.content?.meta?.networkMode ||
        orderType.value === 'individual' && template.content?.meta?.networkMode
    ) {
      await setOrderType(template.content?.meta?.networkMode ? 'networking' : 'individual')
    }

    if (template.content && template.name) {
      if (template.id) {
        if (notify) {
          const defaultText = template.default ? 'по умолчанию ' : ''
          $showToast({ title: `Применён шаблон ${defaultText}"${template.name}"` })
        }
        appliedTemplates.value = [...appliedTemplates.value, template.id]
      } else {
        if (notify) {
          $showToast({ title: `Форма заказа очищена` })
        }
        appliedTemplates.value = []
      }
    }

    await getOrderPriceAction()
  }

  async function createDraft() {
    if (orderForm.value?.meta?.draftId) {
      const result = await updateDraft(
          orderForm.value.meta.draftId,
          {
            ...pricePayload(orderForm.value),
            cost: orderTotalPrice.value?.total || 0
          })
      $showToast({
        title: result?.id ? 'Черновик изменён' : result?.error?.message,
        text: '',
        variant: result?.id ? 'success' : 'error'
      })
    } else {
      const result = await createDraftApi(
          {
            ...pricePayload(orderForm.value),
            cost: orderTotalPrice.value?.total || 0
          })
      if (result?.id) {
        orderForm.value.meta.draftId = result.id
        await useRouter().replace({query: {draft: `${result.id}`}})
      }
    }
  }

  function saveToStorage() {
    if (!useRoute().params.guid) {
      if (getPriceRequestCount.value > 3) {
        if (orderType.value === 'multi' && multiOrder.selectedRequestId.value !== 0) {
          return
        }
        console.log('set lastOrderDraftData')
        lastOrderDraftData.value = {
          type: orderType.value,
          order: deepCopyObject(orderForm.value),
          list: lastOrderDraftData.value?.list
        }
      } else {
        getPriceRequestCount.value++
      }
    }
  }

  async function setLoadingManifest() {
    if (orderForm.value.meta?.isLoadingManifestFilled) {
      loadingManifest.value = await getLoadingManifest(orderForm.value.meta.guid)
    } else {
      loadingManifest.value = undefined
    }
  }

  watch(destinationGroupDatesIndex, (v, old) => {
    // игнорируем первое изменение (при заказе из калькуляции/клонировании)
    if (old !== undefined) {
      orderForm.value.directions.destination.calendar.date = ''
      updateTimeTable()
    }
  })

  watch(timetableData, () => {
    updateTimeTable()
  })

  // При изменении timetable запрашиваем getPrice. Кроме тех случаев, когда календарь заблокирован,
  // например при открытии заказа. При открытии - getPrice не нужен, а дальше при редактировании - нужен
  // flush: 'post' - При изменении timetable.value отрабатывают разные watch, нужно их дождаться
  watch(() => timetable.value,
      async () => {
        if (!isCalendarLocked.value) {
          if (isMultiOrder.value) {
            // остальные цены заберутся в useMultiOrder
            await getOrderFullPriceAction()
          } else {
            await getOrderPriceAction()
          }
        } else {
          isCalendarLocked.value = false
        }
      },
      { flush: 'post' }
  )

  watch(() => isFakeLocationDisabled.value, () => {
    if (isFakeLocationDisabled.value) {
      setOrderPrice()
      getOrderPriceApi(pricePayload(orderForm.value), true)
    }
  })

  // Смена валюты в зависимости от стран
  watch(() => [dispatchLocation.value?.country, destinationLocation.value?.country], (value, oldValue) => {
    if (orderForm.value.meta?.guid || !oldValue[0]) {
      return
    }
    if (value.toString() === oldValue.toString()) {
      return
    }
    let newCountry: string|null = null
    let oldCountry: string|null = null
    let otherCountry = ''
    if (value[0] !== oldValue[0]) {
      newCountry = value[0]
      oldCountry = oldValue[0]
      otherCountry = value[1] || ''
    } else if (value[1] !== oldValue[1]) {
      newCountry = value[1]
      oldCountry = oldValue[1]
      otherCountry = value[0]
    } else {
      // swap
      return
    }

    if (
        (newCountry === 'BY' && !orderPriceCurrency.value.hasBYCurrency) ||
        (newCountry === 'KZ' && !orderPriceCurrency.value.hasKZCurrency) ||
        (newCountry === 'KG' && !orderPriceCurrency.value.hasKGCurrency) ||
        (otherCountry === newCountry)
    ) {
      orderForm.value.currency = newCountry
    } else if (newCountry === 'RU') {
      orderForm.value.currency = otherCountry || 'RU'
    }
  })

  // Инициализация
  getWaitingData().then(timeList => waitingTimeList.value = timeList)

  return {
    /**getters */
    dispatchLocation,
    isDetailCorrectOrderView,
    destinationLocation,
    networkingAddress,
    orderWarnings,
    orderType,
    loadingManifest,
    isLoadingFullPrice,
    hideOrderWrappingOverload,
    hideOrderWrappingTurnOver,
    payerText,
    timetable,
    waitingTimeList,
    orderPrice,
    orderTotalPrice,
    services,
    orderTotalPriceLoader,
    destinationGroupDatesIndex,
    splitTime,
    splitIndex,
    orderForm,
    orderView,
    locationMaxFloor,
    orderFormV3,
    placeItems,
    codList,
    codDate,
    codOrder,
    orderPriceCurrency,
    isCalendarLocked,
    orderDates,
    isTemplate,
    getPriceRequestCount,
    appliedTemplates,
    timetablePayload,
    skipDefaultTimeFlag,
    orderConditionValues,
    orderCreateContent,
    orderCargoConditionHint,
    costDisabledReason,
    isFakeLocationDisabled,
    lastOrderDraftData,
    lightOrderStep,
    hasPupCost,
    specialPupPvz,

    /** submodules */
    multiOrder,

    /**actions */
    updateTimeTable,
    getOrderPriceAction,
    setOrderPrice,
    setOrderType,
    setTradingNetwork,
    setTradingNetworkV3,
    setOrderWarnings,
    setOrderForm,
    setEmptyOrder,
    updateSplit,
    updateOrderPrice,
    setDefaultDate,
    applyTemplate,
    createDraft,
    onOrderTypeChange,
    saveToStorage,
    setLoadingManifest,
  }
})
